import { MapReference } from "@shared/types";
import styles from "../../styles";
import { GameRouteService } from "../../services/GameRouteService";

/**
 * Interface for map layout data
 */
interface MapLayout {
    x: number;
    y: number;
    width: number;
    height: number;
    not_available?: boolean;
}

/**
 * Extended MapReference interface with layout and available properties
 */
interface ExtendedMapReference extends MapReference {
    layout?: MapLayout;
    available: boolean;
}

/**
 * Component for rendering the prison map
 */
export class MapComponent extends HTMLElement {
    private _rooms: ExtendedMapReference[] = [];
    private _currentRoomAlias: string = "";
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    private _onRoomChange?: (roomAlias: string) => void;

    // Define room layouts once as a static property to avoid duplication
    private static readonly roomLayouts: Record<string, MapLayout> = {
        toilets: { x: 15, y: 15, width: 225, height: 45 },
        kitchen: { x: 255, y: 15, width: 225, height: 45 },
        eating: { x: 15, y: 75, width: 300, height: 135 },
        garbage: { x: 330, y: 75, width: 150, height: 60 },
        washing_machine: { x: 330, y: 150, width: 150, height: 60 },
        cell1: { x: 15, y: 225, width: 60, height: 60, not_available: true },
        cell2: { x: 15, y: 300, width: 60, height: 60, not_available: true },
        cell3: { x: 15, y: 375, width: 60, height: 60, not_available: true },
        corridor: { x: 90, y: 225, width: 120, height: 210 },
        cell4: { x: 225, y: 225, width: 60, height: 60, not_available: true },
        cell: { x: 225, y: 300, width: 60, height: 60 },
        cell6: { x: 225, y: 375, width: 60, height: 60, not_available: true },
        gym: { x: 15, y: 450, width: 270, height: 120 },
        visitor_center: { x: 15, y: 585, width: 180, height: 90 },
        post: { x: 210, y: 585, width: 75, height: 90 },
    };

    /**
     * Set the rooms data for the map
     */
    public set rooms(rooms: MapReference[]) {
        // Convert MapReference to ExtendedMapReference with default values
        this._rooms = rooms.map(room => ({
            ...room,
            layout: this.getLayoutForRoom(room.alias),
            available: true, // Default to available
        }));
        this.render();
    }

    /**
     * Get layout for a specific room
     */
    private getLayoutForRoom(alias: string): MapLayout | undefined {
        return MapComponent.roomLayouts[alias];
    }

    /**
     * Set the current room alias
     */
    public set currentRoomAlias(alias: string) {
        this._currentRoomAlias = alias;
        this.render();
    }

    /**
     * Set the room change callback
     */
    public set onRoomChange(callback: (roomAlias: string) => void) {
        this._onRoomChange = callback;
    }

    /**
     * Called when the element is connected to the DOM
     */
    public connectedCallback(): void {
        if (this.innerHTML === "") {
            this.render();
        }

        requestAnimationFrame(() => {
            this.setupRoomListeners();
        });
    }

    /**
     * Render the component
     */
    private render(): void {
        const roomsHtml: string = this.renderPrisonMapLayout();

        if (roomsHtml === "") {
            return;
        }

        const content: string = `
            <style>${styles.map}</style>
            <div class="prison-map-container">
                <div class="prison-map">
                    ${roomsHtml}
                    <div class="map-legend">
                        <div class="legend-item">
                            <div class="legend-color current"></div>
                            <div class="legend-text">Current Location</div>
                        </div>
                        <div class="legend-item">
                            <div class="legend-color unlocked"></div>
                            <div class="legend-text">Available</div>
                        </div>
                        <div class="legend-item">
                            <div class="legend-color locked"></div>
                            <div class="legend-text">Locked</div>
                        </div>
                        <div class="legend-item">
                            <div class="legend-color not-available-room"></div>
                            <div class="legend-text">Not Available</div>
                        </div>
                    </div>
                </div>
            </div>
        `;
        this.innerHTML = content;
    }

    /**
     * Setup event listeners for room shapes
     */
    private setupRoomListeners(): void {
        // Find all room shapes within this component instance
        const roomShapes: NodeListOf<HTMLElement> = this.querySelectorAll(".room-shape");
        roomShapes.forEach(roomShape => {
            roomShape.addEventListener("click", async (e: Event) => {
                e.stopPropagation(); // Prevent event bubbling
                const roomElement: HTMLElement = e.currentTarget as HTMLElement;
                const roomAlias: string | null = roomElement.dataset.room || null;
                const isLocked: boolean = roomElement.classList.contains("locked") ||
                  roomElement.classList.contains("not-available-room");

                if (roomAlias && !isLocked) {
                    // Find the actual room alias from the available rooms
                    const actualRoom: ExtendedMapReference | undefined = this._rooms.find(r =>
                        r.alias === roomAlias ||
                        r.alias.replace("-", "_") === roomAlias ||
                        roomAlias.replace("_", "-") === r.alias
                    );

                    if (actualRoom && actualRoom.alias !== this._currentRoomAlias) {
                        try {
                            // If a callback is provided, use it
                            if (this._onRoomChange) {
                                this._onRoomChange(actualRoom.alias);
                            }
                            else {
                                // Otherwise use the game route service directly
                                await this._gameRouteService.requestRoomChange(actualRoom.alias);

                                // Dispatch a custom event that can be listened to by parent components
                                this.dispatchEvent(new CustomEvent("roomChanged", {
                                    detail: { roomAlias: actualRoom.alias },
                                    bubbles: true,
                                    composed: true,
                                }));

                                // Find and close the popup if it exists
                                const popup: HTMLElement | null = this.closest("game-popup");
                                if (popup) {
                                    popup.remove();
                                }
                            }
                        }
                        catch (error) {
                            console.error("Error changing rooms:", error);
                        }
                    }
                }
            });
        });
    }

    /**
     * Render the prison map layout
     */
    private renderPrisonMapLayout(): string {
        // If no rooms are set, return empty string
        if (this._rooms.length === 0) {
            return "";
        }

        let mapHtml: string = "";

        // Add prison outline
        mapHtml += "<div class=\"prison-outline\"></div>";

        // Create document fragment for better performance
        const roomElements: string[] = [];

        // Iterate through all possible rooms
        for (const [roomAlias, layout] of Object.entries(MapComponent.roomLayouts)) {
            // Check if this room is available by matching with any of the available rooms
            const matchingRoom: ExtendedMapReference | undefined = this._rooms.find(r =>
                r.alias === roomAlias ||
                r.alias.replace("-", "_") === roomAlias ||
                roomAlias.replace("_", "-") === r.alias
            );

            const isAvailable: boolean = !!matchingRoom;
            const isCurrent: boolean = roomAlias.replace(/_/g, "-") === this._currentRoomAlias;
            const roomName: string = matchingRoom?.name || roomAlias.replace(/_/g, " ");

            const roomClass: string = layout.not_available !== true
                ? isCurrent
                    ? "current"
                    : (isAvailable ? "unlocked" : "locked")
                : "not-available-room";

            roomElements.push(`
                <div class="room-shape ${roomClass}" 
                     data-room="${roomAlias}"
                     style="left: ${layout.x}px; top: ${layout.y}px; width: ${layout.width}px; height: ${layout.height}px;">
                    <div class="map-room-name">${roomName}</div>
                </div>
            `);
        }

        // Join all room elements
        mapHtml += roomElements.join("");

        return mapHtml;
    }
}
